import React from 'react';
import { Box, Stack, Typography, Skeleton, Card } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';

interface YearData {
    year: string;
    isPaid: boolean;
    hasIssues: boolean;
    issueCount: number;
    taxableIncome: number | null;
    transactionCount: number;
    isLoading?: boolean;
}

interface YearSelectorCardsProps {
    years: string[];
    selectedYear: string;
    onYearSelect: (year: string) => void;
    yearDataMap: Record<string, YearData>;
    isLoading?: boolean; // Loading tax reports
}

const YearSelectorCards: React.FC<YearSelectorCardsProps> = ({
    years,
    selectedYear,
    onYearSelect,
    yearDataMap,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();

    // Get payment status icon
    const getPaymentIcon = (yearData: YearData) => {
        if (!yearData) return null;
        
        if (yearData.isPaid) {
            return (
                <LockOpenIcon sx={{ 
                    color: 'success.main',
                    fontSize: { xs: '1.1rem', md: '1.2rem' }
                }} />
            );
        } else {
            return (
                <LockIcon sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '1.1rem', md: '1.2rem' }
                }} />
            );
        }
    };

    // Check if year has issues (for warning indicator)
    const hasIssues = (yearData: YearData) => {
        return yearData && yearData.hasIssues && yearData.issueCount > 0;
    };

    // Format currency amount
    const formatAmount = (amount: number | null) => {
        if (amount === null || amount === undefined) return '---';
        return `${amount.toFixed(2)} €`;
    };

    const selectedYearData = yearDataMap[selectedYear];

    return (
        <Card
            variant="outlined"
            sx={{
                minWidth: { xs: 'auto', md: 240 },
                maxWidth: { xs: '100%', md: 240 },
                mt: { xs: 3, md: 0 }, // Extra margin on mobile to create gap from navigation
                height: 'fit-content',
            }}
        >
            {/* Sidebar Header */}
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {t('taxReports.selectTaxYear', 'Steuerjahr auswählen')}
            </Typography>
            
            {/* Year Selection - Card Layout */}
            <Stack spacing={2}>
                {isLoading ? (
                    // Show skeleton loading
                    Array.from({ length: 3 }).map((_, index) => (
                        <Card key={`skeleton-${index}`} variant="outlined" sx={{}}>
                            <Box sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}>
                                <Skeleton width="60px" height="24px" />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Skeleton width={18} height={18} variant="circular" />
                                    <Skeleton width={18} height={18} variant="circular" />
                                </Box>
                            </Box>
                        </Card>
                    ))
                ) : years.length === 0 ? (
                    // Show empty state
                    <Card variant="outlined" sx={{}}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('taxReports.noReports', 'No tax reports available')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('taxReports.addIntegrationsFirst', 'Please add integrations first')}
                            </Typography>
                        </Box>
                    </Card>
                ) : (
                    years.map((year) => {
                        const yearData = yearDataMap[year];
                        const paymentIcon = getPaymentIcon(yearData);
                        const hasWarning = hasIssues(yearData);
                        const isSelected = year === selectedYear;
                        
                        return (
                            <Card 
                                key={year}
                                variant="outlined"
                                sx={{ 
                                    cursor: 'pointer',
                                    border: isSelected ? '2px solid #D35400' : '1px solid transparent',
                                    bgcolor: isSelected ? 'rgba(255, 200, 150, 0.05)' : 'background.paper',
                                }}
                                onClick={() => onYearSelect(year)}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <Typography 
                                        variant="h6" 
                                        sx={{
                                            color: isSelected ? '#D35400' : 'text.primary'
                                        }}
                                    >
                                        {year}
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {/* Warning indicator for issues */}
                                        {hasWarning && (
                                            <NotificationsOutlinedIcon sx={{ 
                                                color: 'warning.main',
                                                fontSize: '1.1rem',
                                            }} />
                                        )}
                                        
                                        {/* Payment status icon */}
                                        {paymentIcon}
                                    </Box>
                                </Box>
                            </Card>
                        );
                    })
                )}
            </Stack>
        </Card>
    );
};

export default YearSelectorCards;